import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { removeJSONFromMessage } from '../../utils/campaignUtils';

const Message = ({ message, isTyping }) => {
  const isAI = message.role === 'assistant';
  const isSystem = message.role === 'system';
  
  // Only handle content formatting - no campaign extraction
  const preprocessContent = (content) => {
    if (!content) return '';
    
    // Just remove JSON content from display - let parent handle campaign extraction
    return removeJSONFromMessage(content, 'v2_campaign');
  };

  // Custom components for react-markdown
  const markdownComponents = {
    // Customize code blocks
    code: ({ inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && language) {
        return (
          <div className="my-3 rounded-md overflow-hidden">
            <div className="bg-gray-800 text-xs text-gray-200 px-4 py-1 flex justify-between items-center">
              <span>{language}</span>
              <button
                onClick={() => navigator.clipboard.writeText(String(children))}
                className="text-gray-400 hover:text-white"
              >
                Copy
              </button>
            </div>
            <pre className="bg-gray-900 p-4 overflow-x-auto text-xs text-gray-100 font-mono">
              <code>{children}</code>
            </pre>
          </div>
        );
      }
      
      // Inline code
      return (
        <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-sm" {...props}>
          {children}
        </code>
      );
    },
    
    // Customize links
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline break-all hover:no-underline transition-colors ${
          (isAI || isSystem)
            ? 'text-blue-600 hover:text-blue-800' 
            : 'text-white hover:text-blue-100'
        }`}
        {...props}
      >
        {children}
      </a>
    ),
    
    // Customize headings
    h1: ({ children, ...props }) => (
      <h1 className={`text-xl font-bold mt-6 mb-4 ${(isAI || isSystem) ? 'text-gray-900' : 'text-white'}`} {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className={`text-lg font-semibold mt-6 mb-4 ${(isAI || isSystem) ? 'text-gray-900' : 'text-white'}`} {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className={`text-base font-medium mt-6 mb-4 ${(isAI || isSystem) ? 'text-gray-900' : 'text-white'}`} {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className={`font-medium mt-6 mb-4 ${(isAI || isSystem) ? 'text-gray-900' : 'text-white'}`} {...props}>
        {children}
      </h4>
    ),
    
    // Customize lists
    ul: ({ children, ...props }) => (
      <ul className="my-4 ml-5 list-disc" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="my-4 ml-5 list-decimal" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li className="mb-1" {...props}>
        {children}
      </li>
    ),
    
    // Customize paragraphs
    p: ({ children, ...props }) => (
      <p className={`mb-3 ${(isAI || isSystem) ? 'text-gray-800' : 'text-white'}`} {...props}>
        {children}
      </p>
    ),
    
    // Customize strong/bold
    strong: ({ children, ...props }) => (
      <strong className="font-semibold" {...props}>
        {children}
      </strong>
    ),
    
    // Customize emphasis/italic
    em: ({ children, ...props }) => (
      <em className="italic" {...props}>
        {children}
      </em>
    )
  };

  return (
    <div className={`mb-3 sm:mb-4 ${isAI ? 'flex flex-col items-start' : 'flex items-end justify-end'}`}>
      {isAI && (
        <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-[#0F31F7] flex items-center justify-center mb-2">
          <img src="/isologo.blanco.png" alt="CH3" className="h-3 w-3 sm:h-4 sm:w-4 object-contain" />
        </div>
      )}

      <div className={`px-3 py-2 sm:px-4 sm:py-3 shadow-sm 
        ${isAI ? 'bg-white rounded-2xl border border-gray-200 text-gray-800 w-full max-w-[95%] sm:max-w-[90%]' :
          isSystem ? 'bg-white rounded-2xl border border-gray-200 text-gray-800 max-w-[85%] sm:max-w-[80%]' :
            'bg-[#0F31F7] rounded-2xl rounded-br-md text-white max-w-[85%] sm:max-w-[80%]'
        }`}>
        {isTyping ? (
          <div className="flex flex-col gap-1 sm:gap-2">
            <div className="flex items-center gap-1 py-1 sm:py-2">
              {[0, 150, 300].map((delay, index) => (
                <div 
                  key={index}
                  className="w-1.5 h-1.5 bg-[#0F31F7] bg-opacity-80 rounded-full animate-bounce"
                  style={{ animationDelay: `${delay}ms` }} 
                />
              ))}
            </div>
            <span className="text-xs text-gray-500">Pensando...</span>
          </div>
        ) : (
          <>
            <div className={`leading-relaxed ${(isAI || isSystem) ? 'font-normal text-gray-800 text-xs sm:text-sm' : 'text-white text-xs sm:text-sm'
              }`}>
              <ReactMarkdown 
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
              >
                {preprocessContent(message.content)}
              </ReactMarkdown>
            </div>
            {message.timestamp && (
              <div className="text-[8px] sm:text-[10px] mt-1 sm:mt-1.5 text-inherit opacity-60">
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Message;